import { DEFAULT_LEAD_TIME_BUFFER_SEC } from './sender.entity';

export const SENDER_EVENT_TYPE_LABELS: Record<string, string> = {
	run_started: 'Старт',
	run_resumed: 'Возобновление',
	broadcast_started: 'Рассылка',
	session_validate_ok: 'Сессия OK',
	session_validate_failed: 'Ошибка сессии',
	proxy_selected: 'Прокси выбран',
	proxy_check_failed: 'Прокси не прошёл проверку',
	proxy_connect_failed: 'Прокси: ошибка подключения',
	proxy_exhausted: 'Прокси исчерпаны',
	proxy_list_empty: 'Список прокси пуст',
	audience_resolved: 'Аудитория',
	channel_resolved: 'Канал',
	channel_post_uploaded: 'Пост в канале',
	external_channel_resolved: 'Внешний канал',
	burst_scheduled: 'Залп',
	schedule_date_adjusted: 'Коррекция времени залпа',
	schedule_progress: 'Постановка',
	schedule_peer_skipped: 'Пропуск peer',
	schedule_complete: 'Планирование',
	schedule_aborted: 'Остановка постановки',
	delivery_progress: 'Доставка',
	delivery_waiting_schedule_date: 'Ожидание отправки',
	burst_fired: 'Залп отправлен',
	delivery_complete: 'Доставка',
	cleanup_started: 'Очистка',
	cleanup_web_code_requested: 'Код web_delete',
	cleanup_web_code_received: 'Код получен',
	cleanup_web_code_timeout: 'Таймаут кода',
	cleanup_ok: 'Очистка OK',
	cleanup_skipped: 'Сессия сохранена',
	cleanup_failed: 'Ошибка очистки',
	run_completed: 'Завершение',
	run_cancelled: 'Отмена',
	flood_wait: 'FloodWait',
};

export type SenderEventCategory = 'error' | 'success' | 'progress' | 'info';

const SENDER_SUCCESS_EVENT_TYPES = new Set(['cleanup_ok', 'run_completed', 'broadcast_started']);

const SENDER_PROGRESS_EVENT_TYPES = new Set(['schedule_progress', 'delivery_progress']);

/** Legacy / technical events hidden from operator timeline. */
export const SENDER_HIDDEN_EVENT_TYPES = new Set([
	'channel_resolved',
	'channel_post_uploaded',
	'schedule_peer_skipped',
	'schedule_complete',
	'delivery_complete',
	'cleanup_started',
	'cleanup_web_code_requested',
	'cleanup_web_code_received',
	'session_validate_ok',
]);

const FAILURE_REASON_LABELS: Record<string, string> = {
	no_messages: 'в чате 777000 нет сообщений',
	no_new_messages: 'новых сообщений после запроса кода нет',
	marker_without_code: 'есть похожее сообщение, но токен не распознан',
	all_excluded: 'все найденные коды уже использованы',
	timeout: 'код не появился за отведённое время',
};

const CLEANUP_ERROR_LABELS: Record<string, string> = {
	captcha: 'требуется captcha на my.telegram.org',
	auth_login_failed: 'неверный код входа',
	empty_login_code: 'пустой код',
	auth_send_password_invalid: 'некорректный ответ send_password',
	delete_hash_missing: 'не найден hash страницы удаления',
};

const SKIP_REASON_LABELS: Record<string, string> = {
	banned: 'бан',
	no_rights: 'нет прав',
	peer_flood: 'PEER_FLOOD',
	unknown: 'прочее',
};

const SCHEDULE_ABORT_REASON_LABELS: Record<string, string> = {
	consecutive_schedule_errors: 'Остановлено после серии ошибок постановки',
	peer_flood: 'Остановлено из-за PEER_FLOOD',
	flood: 'Остановлено из-за флуда',
	auth: 'Остановлено: сессия не авторизована',
	schedule_deadline: 'Остановлено: не успели поставить отложку до времени залпа',
};

const UNKNOWN_SCHEDULE_ABORT_LABEL = 'неизвестная причина остановки';

export function formatScheduleAbortReason(reason: string | undefined): string | undefined {
	if (!reason) return undefined;
	return SCHEDULE_ABORT_REASON_LABELS[reason] ?? UNKNOWN_SCHEDULE_ABORT_LABEL;
}

const SOURCE_LABELS: Record<string, string> = {
	public_channel: 'через свой публичный канал',
	external_channel: 'из внешнего канала',
};

const CONTENT_TYPE_LABELS: Record<string, string> = {
	apk_document: 'APK-файл',
	document: 'документ',
	text: 'текст',
	photo: 'фото',
	video: 'видео',
	audio: 'аудио',
	media: 'медиа',
	channel_message: 'сообщение из канала',
};

export const SENDER_ERROR_EVENT_TYPES = new Set([
	'session_validate_failed',
	'schedule_aborted',
	'cleanup_web_code_timeout',
	'cleanup_failed',
	'proxy_check_failed',
	'proxy_connect_failed',
	'proxy_exhausted',
	'proxy_list_empty',
]);

function asNumber(value: unknown): number | undefined {
	return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function asString(value: unknown): string | undefined {
	return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function asBoolean(value: unknown): boolean | undefined {
	return typeof value === 'boolean' ? value : undefined;
}

function formatPayloadValue(value: unknown): string | undefined {
	const asText = asString(value);
	if (asText != null) return asText;
	const asNum = asNumber(value);
	if (asNum != null) return String(asNum);
	return undefined;
}

function formatFailureReason(reason: string | undefined): string {
	if (!reason) return FAILURE_REASON_LABELS.timeout;
	return FAILURE_REASON_LABELS[reason] ?? reason;
}

function formatScheduledProgress(count: number | undefined, total: number | undefined): string | null {
	if (count == null || total == null) return null;
	return `Запланировано ${count} из ${total}`;
}

function formatDeliveryProgress(
	count: number | undefined,
	total: number | undefined,
	remaining?: number | undefined,
): string | null {
	if (count == null || total == null) return null;
	const base = `Доставлено ${count} из ${total}`;
	if (remaining == null) return base;
	return `${base}, осталось ${remaining}`;
}

export function extractSessionPhone(events: { eventType: string; payload: Record<string, unknown> }[]): string | undefined {
	for (const event of events) {
		if (event.eventType === 'session_validate_ok' || event.eventType === 'broadcast_started') {
			const phone = asString(event.payload.phone);
			if (phone) return phone;
		}
	}
	return undefined;
}

export function formatSenderEventSummary(
	eventType: string,
	payload: Record<string, unknown> = {},
	options?: { occurredAt?: string; audienceTarget?: number },
): string | null {
	switch (eventType) {
		case 'run_started':
			return 'Запущено сессий: 1';
		case 'run_resumed':
			return 'Рассылка возобновлена с checkpoint';
		case 'run_cancelled': {
			const reason = asString(payload.reason);
			return reason ? `Запуск отменён: ${reason}` : 'Запуск отменён';
		}
		case 'flood_wait': {
			const seconds = asNumber(payload.seconds);
			const phase = asString(payload.phase);
			if (seconds == null) return null;
			return phase ? `Пауза ${seconds}с (фаза ${phase})` : `Пауза ${seconds}с`;
		}
		case 'broadcast_started': {
			const source = SOURCE_LABELS[asString(payload.source) ?? ''] ?? asString(payload.source) ?? '—';
			const contentType = asString(payload.content_type) ?? 'apk_document';
			const formatLabel =
				CONTENT_TYPE_LABELS[contentType] ??
				(asString(payload.source) === 'external_channel' ? 'сообщение из канала' : contentType);
			return `Рассылка начата · источник: ${source} · формат: ${formatLabel} · без бота`;
		}
		case 'external_channel_resolved': {
			const url = asString(payload.url);
			const messageId = asNumber(payload.message_id);
			if (url && messageId != null) {
				return `Канал ${url} · сообщение #${messageId}`;
			}
			return url ?? 'Внешний канал разрешён';
		}
		case 'session_validate_failed':
			return asString(payload.error) ?? asString(payload.reason) ?? 'Сессия не прошла валидацию';
		case 'proxy_selected': {
			const proxy = asString(payload.proxy) ?? '—';
			const attempt = asNumber(payload.attempt);
			return attempt != null ? `${proxy} (попытка ${attempt})` : proxy;
		}
		case 'proxy_check_failed':
		case 'proxy_connect_failed': {
			const proxy = asString(payload.proxy) ?? '—';
			const error = asString(payload.error);
			return error ? `${proxy}: ${error}` : proxy;
		}
		case 'proxy_exhausted':
		case 'proxy_list_empty':
			return asString(payload.error) ?? 'Прокси недоступны';
		case 'audience_resolved': {
			const target = asNumber(payload.target_count);
			const total = asNumber(payload.total);
			const nonMutual = asNumber(payload.contacts_non_mutual);
			if (target == null || total == null) return null;
			if (nonMutual != null && nonMutual > 0) {
				return `Время залпа считаем по ${target} целям (${total} всего − ${nonMutual} невзаимных)`;
			}
			return `Время залпа считаем по ${target} целям`;
		}
		case 'burst_scheduled': {
			const lead = asNumber(payload.lead_time_sec);
			const posting = asNumber(payload.posting_estimate_sec);
			const bufferSec = DEFAULT_LEAD_TIME_BUFFER_SEC;
			const scheduleDateRaw = asString(payload.schedule_date);
			if (scheduleDateRaw) {
				const scheduleAt = new Date(scheduleDateRaw);
				if (!Number.isNaN(scheduleAt.getTime())) {
					const timeLabel = scheduleAt.toLocaleTimeString('ru-RU', {
						hour: '2-digit',
						minute: '2-digit',
					});
					const occurredAtMs = options?.occurredAt ? Date.parse(options.occurredAt) : Number.NaN;
					let totalSec: number | undefined;
					if (Number.isFinite(occurredAtMs)) {
						totalSec = Math.max(0, Math.round((scheduleAt.getTime() - occurredAtMs) / 1000));
					} else if (lead != null) {
						totalSec = Math.round(lead + bufferSec);
					}
					if (totalSec != null) {
						const postingHint =
							posting != null && posting > 0
								? `, планирование ~${Math.round(posting)} с`
								: '';
						return `Отправка в ${timeLabel} (через ~${totalSec} с${postingHint})`;
					}
					return `Отправка в ${timeLabel}`;
				}
			}
			if (lead == null) return null;
			return `Залп всем отложен на ${lead}с`;
		}
		case 'schedule_date_adjusted': {
			const originalRaw = asString(payload.original);
			const adjustedRaw = asString(payload.adjusted);
			if (!originalRaw || !adjustedRaw) return 'Время залпа скорректировано';
			const originalAt = new Date(originalRaw);
			const adjustedAt = new Date(adjustedRaw);
			if (Number.isNaN(originalAt.getTime()) || Number.isNaN(adjustedAt.getTime())) {
				return 'Время залпа скорректировано (posting overrun)';
			}
			const fmt = (d: Date) =>
				d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
			return `Время залпа: ${fmt(originalAt)} → ${fmt(adjustedAt)} (posting overrun)`;
		}
		case 'schedule_progress':
			return formatScheduledProgress(asNumber(payload.scheduled_count), asNumber(payload.target_count));
		case 'delivery_progress':
			return formatDeliveryProgress(
				asNumber(payload.delivered_count),
				asNumber(payload.scheduled_count),
				asNumber(payload.remaining_count),
			);
		case 'delivery_waiting_schedule_date': {
			const remaining = asNumber(payload.remaining_sec);
			if (remaining == null) return 'Ожидание времени отправки';
			return `Отложенные отправятся через ~${remaining}с`;
		}
		case 'burst_fired': {
			const scheduled = asNumber(payload.scheduled_count);
			const scheduleDateRaw = asString(payload.schedule_date);
			if (scheduleDateRaw) {
				const scheduleAt = new Date(scheduleDateRaw);
				if (!Number.isNaN(scheduleAt.getTime())) {
					const timeLabel = scheduleAt.toLocaleTimeString('ru-RU', {
						hour: '2-digit',
						minute: '2-digit',
					});
					return scheduled != null
						? `Залп в ${timeLabel} · ${scheduled} получателей`
						: `Залп в ${timeLabel}`;
				}
			}
			return scheduled != null ? `Залп отправлен · ${scheduled} получателей` : 'Залп отправлен';
		}
		case 'delivery_complete': {
			const delivered = asNumber(payload.delivered_count);
			const scheduled = asNumber(payload.scheduled_count);
			const assumed = asBoolean(payload.assumed);
			const line = formatDeliveryProgress(delivered, scheduled);
			if (line == null) return null;
			if (assumed) return `${line} (без TG poll)`;
			return line;
		}
		case 'schedule_peer_skipped': {
			const reason = asString(payload.reason);
			return `peer ${formatPayloadValue(payload.peer_id) ?? '—'}: ${reason ? (SKIP_REASON_LABELS[reason] ?? reason) : 'пропуск'}`;
		}
		case 'schedule_complete': {
			const planned = asNumber(payload.planned_count);
			const skipped = asNumber(payload.skipped_count);
			if (planned == null) return null;
			return `Запланировано ${planned}${skipped ? `, пропущено ${skipped}` : ''}`;
		}
		case 'schedule_aborted': {
			const reason = asString(payload.reason) ?? 'unknown';
			const count = asNumber(payload.scheduled_count_at_abort);
			const unattempted = asNumber(payload.unattempted_count);
			const label = formatScheduleAbortReason(reason) ?? UNKNOWN_SCHEDULE_ABORT_LABEL;
			const parts = [label];
			if (count != null) parts.push(`запланировано ${count}`);
			if (unattempted != null && unattempted > 0) {
				parts.push(`не поставлено ${unattempted}`);
			}
			return parts.join(', ');
		}
		case 'cleanup_web_code_timeout': {
			const waited = asNumber(payload.waited_sec);
			const reason = formatFailureReason(asString(payload.failure_reason));
			const fresh = asNumber(payload.messages_fresh);
			const marker = asNumber(payload.messages_with_marker ?? payload.messages_parsed);
			const parts = [reason];
			if (waited != null) parts.push(`ожидание ${waited.toFixed(0)}с`);
			if (fresh != null) parts.push(`свежих: ${fresh}`);
			if (marker != null) parts.push(`с маркером: ${marker}`);
			return parts.join(' · ');
		}
		case 'cleanup_ok':
			return 'удалён через web';
		case 'cleanup_failed': {
			const error = asString(payload.error) ?? 'unknown';
			return CLEANUP_ERROR_LABELS[error] ?? error;
		}
		case 'run_completed': {
			const delivered = asNumber(payload.delivered_count);
			const scheduled = asNumber(payload.scheduled_count);
			const payloadAudienceTarget = asNumber(payload.audience_target);
			const partialSchedule = asBoolean(payload.partial_schedule);
			const deleted = asBoolean(payload.account_deleted);
			if (delivered != null) {
				const audienceTarget =
					payloadAudienceTarget ?? options?.audienceTarget;
				const hasAudienceGap =
					audienceTarget != null && audienceTarget > 0 && delivered < audienceTarget;
				const total =
					hasAudienceGap
						? audienceTarget
						: partialSchedule && audienceTarget != null && audienceTarget > 0
							? audienceTarget
							: scheduled;
				if (total != null) {
					const tail = deleted ? ' аккаунт Telegram самоудалился.' : '.';
					const partialNote =
						hasAudienceGap || partialSchedule ? ' Частичная рассылка.' : '';
					return `Готово. Отправил ${delivered} из ${total}.${partialNote}${tail}`;
				}
			}
			const duration = asNumber(payload.duration_sec);
			return duration != null ? `Статус ${asString(payload.status) ?? 'ok'}, ${duration.toFixed(0)}с` : null;
		}
		default:
			return null;
	}
}

export function formatSenderEventLine(
	eventType: string,
	payload: Record<string, unknown> = {},
	options?: { occurredAt?: string; audienceTarget?: number },
): string | null {
	return formatSenderEventSummary(eventType, payload, options);
}

export function getSenderEventCategory(eventType: string): SenderEventCategory {
	if (SENDER_ERROR_EVENT_TYPES.has(eventType)) return 'error';
	if (SENDER_SUCCESS_EVENT_TYPES.has(eventType)) return 'success';
	if (SENDER_PROGRESS_EVENT_TYPES.has(eventType)) return 'progress';
	return 'info';
}

export function getSenderEventProgress(
	eventType: string,
	payload: Record<string, unknown> = {},
): { value: number; max: number } | null {
	switch (eventType) {
		case 'schedule_progress': {
			const value = asNumber(payload.scheduled_count);
			const max = asNumber(payload.target_count);
			if (value == null || max == null || max <= 0) return null;
			return { value, max };
		}
		case 'delivery_progress': {
			const value = asNumber(payload.delivered_count);
			const max = asNumber(payload.scheduled_count);
			if (value == null || max == null || max <= 0) return null;
			return { value, max };
		}
		default:
			return null;
	}
}

export function shouldShowSenderEventPayload(
	eventType: string,
	payload: Record<string, unknown> = {},
): boolean {
	if (SENDER_ERROR_EVENT_TYPES.has(eventType)) return true;
	if (asString(payload.error) || asString(payload.failure_reason)) return true;
	return Object.keys(payload).length > 0 && formatSenderEventSummary(eventType, payload) == null;
}

export function formatSenderEventDetails(
	eventType: string,
	payload: Record<string, unknown> = {},
): string | null {
	if (eventType === 'cleanup_web_code_timeout') {
		const preview = asString(payload.marker_preview);
		const floor = asNumber(payload.floor_message_id);
		const notBefore = asString(payload.not_before);
		const lines: string[] = [];
		if (preview) lines.push(`Превью: ${preview}`);
		if (floor != null) lines.push(`floor_message_id: ${floor}`);
		if (notBefore) lines.push(`not_before: ${notBefore}`);
		const newest = asString(payload.newest_message_date);
		if (newest) lines.push(`newest_message_date: ${newest}`);
		return lines.length > 0 ? lines.join('\n') : null;
	}
	return null;
}
